import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Edit, Eye, Share2, Send, Play, Image
} from 'lucide-react';
import HubLayout from '../common/HubLayout';
import { useFamily } from '../../contexts/FamilyContext';
import { getMyTimeCapsules, getReceivedTimeCapsules, getPendingCapsuleCount, deliverCapsule, getFamilyCapsuleActivity } from '../../services/api';
import { toast } from 'react-toastify';
import CapsuleViewer from './CapsuleViewer';
import MysteryVault from './MysteryVault';
import { getUser } from '../../utils/secureStorage';

// Color constants matching the mockup design system
const colors = {
  primary: '#556B2F',
  primaryDark: '#4A5D23',
  primaryLight: '#6B8E23',
  orange: '#E65100',
  orangeLight: '#FF8A50',
  cream: '#F5F5DC',
  creamWarm: '#FAF8F0',
  panel: '#F2F2F2',
  border: '#CCCCCC',
  borderLight: '#E0E0E0',
  text: '#333333',
  textMid: '#666666',
  textLight: '#999999',
  white: '#FFFFFF',
  greenTint: '#F1F8E9',
  greenTint2: '#E8F5E9',
  orangeTint: '#FFF3E0',
  vaultDeep: '#2E3B18',
};

// CSS for animations and fonts
const animationCSS = `
  @import url('https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,400;0,500;0,600;0,700;1,400&family=Source+Sans+3:wght@300;400;500;600;700&display=swap');

  @keyframes pulse-dot {
    0%, 100% { box-shadow: 0 0 0 0 rgba(230,81,0,0.3); }
    50% { box-shadow: 0 0 0 6px rgba(230,81,0,0); }
  }
  @keyframes shimmer {
    0% { left: -60%; }
    100% { left: 130%; }
  }
  @keyframes sparklePop {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.15); }
  }

  /* ─── Responsive: Tablet (≤960px) ─── */
  @media (max-width: 960px) {
    .tc-card-grid {
      grid-template-columns: repeat(2, 1fr) !important;
    }
  }

  /* ─── Responsive: Mobile (≤640px) ─── */
  @media (max-width: 640px) {
    .tc-card-grid {
      grid-template-columns: 1fr !important;
    }
    .tc-page {
      padding: 20px 16px 40px !important;
    }
    .tc-header {
      flex-direction: column !important;
      align-items: flex-start !important;
      gap: 12px !important;
    }
    .tc-subtitle {
      padding-left: 0 !important;
    }
    .tc-activity-bar {
      flex-direction: column !important;
    }
    .tc-activity-item {
      border-right: none !important;
      border-bottom: 1px solid #E0E0E0 !important;
    }
    .tc-activity-item:last-child {
      border-bottom: none !important;
    }
    .tc-tabs {
      flex-direction: column !important;
    }
    .tc-tab-sending {
      border-right: none !important;
      border-bottom: 2px solid #CCCCCC !important;
    }
    .tc-tab-sub {
      display: none !important;
    }
    .tc-filter-pills {
      flex-wrap: wrap !important;
    }
    .tc-ready-card {
      flex-wrap: wrap !important;
      gap: 12px !important;
    }
    .tc-ready-btn {
      width: 100% !important;
      text-align: center !important;
      justify-content: center !important;
    }
  }
`;

// Helper: generate avatar color from name
const getAvatarColor = (name) => {
  const palette = ['#8B7355', '#6B8E23', '#8B6914', '#5B7553', '#7B5B3A', '#4A6B8A', '#8B5E3C', '#6B5B7B'];
  let hash = 0;
  for (let i = 0; i < (name || '').length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return palette[Math.abs(hash) % palette.length];
};

// Helper: get initials from name
const getInitials = (firstName, lastName) => {
  return `${(firstName || '')[0] || ''}${(lastName || '')[0] || ''}`.toUpperCase();
};

// ─── Letter Card Component (Sending Tab) ───
const LetterCard = ({ letter, onClick, onEdit, onPreview, onShare, onDeliver, videoThumbnails = {} }) => {
  const mediaFiles = letter.media_files_detail || [];
  const recipientName = `${letter.receiver_first_name || ''} ${letter.receiver_last_name || ''}`.trim() || 'Not set';
  const initials = getInitials(letter.receiver_first_name, letter.receiver_last_name);
  const avatarColor = getAvatarColor(recipientName);
  const isDelivered = letter.delivery_status === 'DELIVERED' || letter.delivery_status === 'VIEWED';

  const getMessagePreview = () => {
    if (!letter.message) return '';
    const div = document.createElement('div');
    div.innerHTML = letter.message;
    const text = div.textContent || div.innerText || '';
    return text.length > 120 ? text.substring(0, 120) + '...' : text;
  };

  const getMilestoneLabel = () => {
    if (letter.milestone_name) return letter.milestone_name;
    if (letter.delivery_event) return letter.delivery_event;
    return null;
  };

  const getStatusConfig = () => {
    const ds = letter.delivery_status;
    const s = letter.status;
    const deliveryDate = letter.delivery_datetime
      ? new Date(letter.delivery_datetime).toLocaleDateString('en-US', {
          year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
        })
      : null;

    if (ds === 'VIEWED') {
      return { label: `Opened${deliveryDate ? ` — ${deliveryDate}` : ''}`, type: 'check', bg: colors.greenTint2, color: colors.primaryDark };
    }
    if (ds === 'DELIVERED') {
      return { label: `Delivered${deliveryDate ? ` — ${deliveryDate}` : ''}`, type: 'check', bg: colors.greenTint2, color: colors.primaryDark };
    }
    if (s === 'DRAFT') return { label: 'Draft', type: 'edit', bg: '#FFF8E1', color: '#F57F17' };
    if (s === 'SCHEDULED' && ds === 'PENDING') return { label: 'In Vault', type: 'lock', bg: colors.greenTint, color: colors.primary };
    return { label: 'Pending', type: 'clock', bg: colors.orangeTint, color: colors.orange };
  };

  const milestoneLabel = getMilestoneLabel();
  const statusConfig = getStatusConfig();

  return (
    <div
      style={{
        background: '#fff',
        border: `1px solid ${colors.borderLight}`,
        borderRadius: '12px',
        transition: 'box-shadow 0.15s',
        cursor: 'pointer',
      }}
      onClick={() => onClick(letter)}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.06)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
    >
      {/* Top: Avatar + Title + Badge + Recipient */}
      <div style={{ padding: '18px 20px 10px', display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
        <div
          style={{
            width: '36px', height: '36px', borderRadius: '50%', backgroundColor: avatarColor,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontWeight: 700, fontSize: '13px', flexShrink: 0,
          }}
        >
          {initials}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <div style={{ fontFamily: "'Lora', serif", fontSize: '16px', fontWeight: 600, color: colors.text }}>
              {letter.title || getMessagePreview().substring(0, 40) || 'Untitled Letter'}
            </div>
            {milestoneLabel && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: '4px',
                fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px',
                padding: '3px 10px', borderRadius: '10px',
                backgroundColor: isDelivered ? colors.orangeTint : colors.greenTint2,
                color: isDelivered ? colors.orange : colors.primary,
              }}>
                {milestoneLabel}
              </span>
            )}
          </div>
          <div style={{ fontSize: '14px', color: colors.textMid }}>For: {recipientName}</div>
        </div>
      </div>

      {/* Photos (if media exists) */}
      {mediaFiles.length > 0 && (
        <div style={{ padding: '0 20px', display: 'flex', gap: '5px', marginBottom: '10px' }}>
          {mediaFiles.slice(0, 3).map((media, idx) => {
            const isVideo = media.media_type === 'video';
            const url = media.thumbnail_url || media.url || media.file;
            const videoThumb = videoThumbnails[media.id];

            return (
              <div
                key={media.id || idx}
                style={{
                  width: '52px', height: '52px', borderRadius: '8px', overflow: 'hidden',
                  backgroundColor: '#ddd', flexShrink: 0, position: 'relative',
                }}
              >
                {isVideo && videoThumb ? (
                  <>
                    <img src={videoThumb} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.3)' }}>
                      <Play size={14} color="white" />
                    </div>
                  </>
                ) : url && !isVideo ? (
                  <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#E0E0D8' }}>
                    {isVideo ? <Play size={14} color="#666" /> : <Image size={14} color="#999" />}
                  </div>
                )}
              </div>
            );
          })}
          {mediaFiles.length > 3 && (
            <div style={{
              width: '52px', height: '52px', borderRadius: '8px', backgroundColor: '#eee',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '12px', fontWeight: 600, color: colors.textMid, flexShrink: 0,
            }}>
              +{mediaFiles.length - 3}
            </div>
          )}
        </div>
      )}

      {/* Preview text (shown when no media) */}
      {mediaFiles.length === 0 && getMessagePreview() && (
        <div style={{
          margin: '0 20px 10px', padding: '10px 14px',
          fontSize: '14px', color: colors.textLight, fontStyle: 'italic',
          backgroundColor: '#fafafa', borderRadius: '6px', borderLeft: `3px solid ${colors.borderLight}`,
        }}>
          "{getMessagePreview()}"
        </div>
      )}

      {/* Meta */}
      <div style={{ padding: '0 20px', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '14px', fontSize: '13px', color: colors.textLight }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
          </svg>
          Written {new Date(letter.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
        </div>
      </div>

      {/* Actions */}
      <div style={{ padding: '0 20px 10px', display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
        {[
          { label: 'Edit', icon: <Edit size={15} />, color: colors.primary, action: (e) => { e.stopPropagation(); onEdit(letter.id); } },
          { label: 'Preview', icon: <Eye size={15} />, color: colors.primary, action: (e) => { e.stopPropagation(); onPreview(letter); } },
          { label: 'Share', icon: <Share2 size={15} />, color: colors.primary, action: (e) => { e.stopPropagation(); onShare && onShare(letter); } },
        ].map((btn) => (
          <button
            key={btn.label}
            onClick={btn.action}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              fontFamily: "'Source Sans 3', sans-serif", fontSize: '14px', fontWeight: 600,
              padding: '8px 12px', borderRadius: '6px', border: 'none',
              background: 'transparent', color: btn.color, cursor: 'pointer',
            }}
          >
            {btn.icon} {btn.label}
          </button>
        ))}
        {!isDelivered && (
          <button
            onClick={(e) => { e.stopPropagation(); onDeliver && onDeliver(letter); }}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              fontFamily: "'Source Sans 3', sans-serif", fontSize: '14px', fontWeight: 600,
              padding: '8px 12px', borderRadius: '6px', border: 'none',
              background: 'transparent', color: colors.orange, cursor: 'pointer',
            }}
          >
            <Send size={15} /> Deliver
          </button>
        )}
      </div>

      {/* Status Bar */}
      <div style={{ padding: '0 20px 18px' }}>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: '6px',
          fontSize: '13px', fontWeight: 600, padding: '7px 14px', borderRadius: '8px',
          backgroundColor: statusConfig.bg, color: statusConfig.color,
        }}>
          {statusConfig.type === 'lock' && (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
            </svg>
          )}
          {statusConfig.type === 'check' && (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
          )}
          {statusConfig.type === 'edit' && (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
          )}
          {statusConfig.label}
        </div>
      </div>
    </div>
  );
};

// ─── Main Dashboard Component ───
export default function TimeCapsuleDashboard() {
  const navigate = useNavigate();
  const { activeFamilyId } = useFamily();

  // Tab state (replaces old 3-view navigation)
  const [activeTab, setActiveTab] = useState('sending');

  // My Letters state
  const [myLetters, setMyLetters] = useState([]);
  const [myLettersFilter, setMyLettersFilter] = useState('all');
  const [myLettersLoading, setMyLettersLoading] = useState(true);

  // For You state
  const [forYouLetters, setForYouLetters] = useState([]);
  const [forYouLoading, setForYouLoading] = useState(true);
  const [pendingCount, setPendingCount] = useState(0);
  const [familyActivity, setFamilyActivity] = useState([]);

  // Shared state
  const [selectedCapsule, setSelectedCapsule] = useState(null);
  const [deliverConfirm, setDeliverConfirm] = useState(null);
  const [isDelivering, setIsDelivering] = useState(false);
  const [deliverChecked, setDeliverChecked] = useState(false);

  // Video thumbnails state
  const [videoThumbnails, setVideoThumbnails] = useState({});

  // Generate video thumbnail from first frame using canvas
  const generateVideoThumbnail = useCallback((videoUrl, mediaId) => {
    if (videoThumbnails[mediaId]) return;

    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.muted = true;
    video.playsInline = true;
    video.preload = 'metadata';

    video.onloadeddata = () => {
      video.currentTime = Math.min(1, video.duration * 0.1);
    };

    video.onseeked = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth || 160;
        canvas.height = video.videoHeight || 120;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const thumbnailUrl = canvas.toDataURL('image/jpeg', 0.7);
        setVideoThumbnails(prev => ({ ...prev, [mediaId]: thumbnailUrl }));
      } catch (err) {
      }
    };

    video.onerror = () => {};
    video.src = videoUrl;
  }, [videoThumbnails]);

  // Generate thumbnails for all videos
  useEffect(() => {
    const allLetters = [...myLetters, ...forYouLetters];
    allLetters.forEach(letter => {
      const mediaFiles = letter.media_files_detail || [];
      mediaFiles.forEach(media => {
        if (media.media_type === 'video' && !videoThumbnails[media.id]) {
          const url = media.file || media.url;
          if (url) generateVideoThumbnail(url, media.id);
        }
      });
    });
  }, [myLetters, forYouLetters, generateVideoThumbnail, videoThumbnails]);

  // Fetch my letters (re-fetch on family switch)
  useEffect(() => {
    const fetchMyLetters = async () => {
      try {
        setMyLettersLoading(true);
        const data = await getMyTimeCapsules();
        setMyLetters(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error fetching my letters:', err);
        toast.error('Failed to load your letters');
      } finally {
        setMyLettersLoading(false);
      }
    };
    fetchMyLetters();
  }, [activeFamilyId]);

  // Fetch letters for you (delivered) and pending count
  useEffect(() => {
    const fetchForYouLetters = async () => {
      try {
        setForYouLoading(true);
        const [data, pendingData] = await Promise.all([
          getReceivedTimeCapsules(),
          getPendingCapsuleCount().catch(() => 0)
        ]);
        setPendingCount(typeof pendingData === 'number' ? pendingData : 0);

        const transformedData = (Array.isArray(data) ? data : []).map(letter => {
          let viewStatus = letter.viewed_at ? 'read' : 'ready';
          return {
            ...letter,
            viewStatus,
            title: letter.title || `Letter from ${letter.sender_first_name || 'Someone'}`,
            senderName: `${letter.sender_first_name || ''} ${letter.sender_last_name || ''}`.trim() || 'Unknown',
          };
        });
        setForYouLetters(transformedData);
      } catch (err) {
        console.error('Error fetching letters for you:', err);
      } finally {
        setForYouLoading(false);
      }
    };
    fetchForYouLetters();

    // Fetch family capsule activity
    getFamilyCapsuleActivity()
      .then(data => setFamilyActivity(data.members || []))
      .catch(() => setFamilyActivity([]));
  }, [activeFamilyId]);

  // Stats for My Letters
  const myLettersStats = useMemo(() => ({
    vault: myLetters.filter(c => c.status === 'SCHEDULED' && c.delivery_status === 'PENDING').length,
    delivered: myLetters.filter(c => c.delivery_status === 'DELIVERED').length,
    opened: myLetters.filter(c => c.delivery_status === 'VIEWED').length,
    drafts: myLetters.filter(c => c.status === 'DRAFT').length,
    all: myLetters.length,
  }), [myLetters]);

  // Stats for For Me Letters
  const forMeStats = useMemo(() => ({
    ready: forYouLetters.filter(l => l.viewStatus === 'ready').length,
    pending: pendingCount,
    opened: forYouLetters.filter(l => l.viewStatus === 'read').length,
    total: forYouLetters.length,
  }), [forYouLetters, pendingCount]);

  // Filtered letters for sending tab
  const filteredMyLetters = useMemo(() => {
    let data = myLetters;
    if (myLettersFilter === 'vault') {
      data = data.filter(c => c.status === 'SCHEDULED' && c.delivery_status === 'PENDING');
    } else if (myLettersFilter === 'delivered') {
      data = data.filter(c => c.delivery_status === 'DELIVERED');
    } else if (myLettersFilter === 'opened') {
      data = data.filter(c => c.delivery_status === 'VIEWED');
    } else if (myLettersFilter === 'draft') {
      data = data.filter(c => c.status === 'DRAFT');
    }
    return data;
  }, [myLetters, myLettersFilter]);

  // Categorized receiving letters
  const readyLetters = useMemo(() => {
    const ready = forYouLetters.filter(l => l.viewStatus === 'ready');
    // Sort by receiver name so letters for the same person are grouped
    return ready.sort((a, b) => {
      const nameA = `${a.receiver_first_name || ''} ${a.receiver_last_name || ''}`.trim().toLowerCase();
      const nameB = `${b.receiver_first_name || ''} ${b.receiver_last_name || ''}`.trim().toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [forYouLetters]);
  const openedLetters = useMemo(() => forYouLetters.filter(l => l.viewStatus === 'read'), [forYouLetters]);

  // Filter state for the "Letters for Me" tab
  const [forMeSearch, setForMeSearch] = useState('');
  const [forMePersonFilter, setForMePersonFilter] = useState('all'); // 'all' or person name

  // Get unique people across all for-you letters (ready + opened)
  const forMePeople = useMemo(() => {
    const seen = new Map();
    [...readyLetters, ...openedLetters].forEach(l => {
      const name = `${l.receiver_first_name || ''} ${l.receiver_last_name || ''}`.trim();
      const senderName = l.senderName || '';
      // Add receivers (for letters to children)
      if (name && !seen.has(`r:${name}`)) seen.set(`r:${name}`, { label: `For ${name}`, key: `r:${name}`, type: 'receiver' });
      // Add senders
      if (senderName && !seen.has(`s:${senderName}`)) seen.set(`s:${senderName}`, { label: `From ${senderName}`, key: `s:${senderName}`, type: 'sender' });
    });
    return Array.from(seen.values());
  }, [readyLetters, openedLetters]);

  // Filtered letters
  const filteredReadyLetters = useMemo(() => {
    return readyLetters.filter(l => {
      const term = forMeSearch.toLowerCase();
      const matchesSearch = !term ||
        (l.title || '').toLowerCase().includes(term) ||
        (l.senderName || '').toLowerCase().includes(term) ||
        (l.receiver_first_name || '').toLowerCase().includes(term) ||
        (l.message || '').toLowerCase().includes(term);
      const matchesPerson = forMePersonFilter === 'all' ||
        (forMePersonFilter.startsWith('r:') && `${l.receiver_first_name || ''} ${l.receiver_last_name || ''}`.trim() === forMePersonFilter.slice(2)) ||
        (forMePersonFilter.startsWith('s:') && (l.senderName || '') === forMePersonFilter.slice(2));
      return matchesSearch && matchesPerson;
    });
  }, [readyLetters, forMeSearch, forMePersonFilter]);

  const filteredOpenedLetters = useMemo(() => {
    return openedLetters.filter(l => {
      const term = forMeSearch.toLowerCase();
      const matchesSearch = !term ||
        (l.title || '').toLowerCase().includes(term) ||
        (l.senderName || '').toLowerCase().includes(term) ||
        (l.receiver_first_name || '').toLowerCase().includes(term) ||
        (l.message || '').toLowerCase().includes(term);
      const matchesPerson = forMePersonFilter === 'all' ||
        (forMePersonFilter.startsWith('r:') && `${l.receiver_first_name || ''} ${l.receiver_last_name || ''}`.trim() === forMePersonFilter.slice(2)) ||
        (forMePersonFilter.startsWith('s:') && (l.senderName || '') === forMePersonFilter.slice(2));
      return matchesSearch && matchesPerson;
    });
  }, [openedLetters, forMeSearch, forMePersonFilter]);

  // ─── Handlers ───

  const handleViewMyLetter = (letter) => {
    setSelectedCapsule({
      ...letter,
      sender_first_name: 'You',
      sender_last_name: '',
      isMyLetter: true,
    });
  };

  const handleEdit = (id) => navigate(`/letter-creation?edit=${id}`);

  const handlePreview = (letter) => {
    setSelectedCapsule({ ...letter, isPreview: true });
  };

  const handleDeliver = (letter) => {
    if (!letter || !letter.id) return;
    if (letter.delivery_status === 'DELIVERED' || letter.delivery_status === 'VIEWED') {
      toast.info('This letter has already been delivered.');
      return;
    }
    setDeliverChecked(false);
    setDeliverConfirm(letter);
  };

  const handleDeliverConfirm = async () => {
    if (!deliverConfirm) return;
    setIsDelivering(true);
    try {
      await deliverCapsule(deliverConfirm.id);
      setMyLetters(prev => prev.map(l =>
        l.id === deliverConfirm.id
          ? { ...l, delivery_status: 'DELIVERED', delivery_datetime: new Date().toISOString() }
          : l
      ));
      setSelectedCapsule(null);
      setDeliverConfirm(null);
      toast.success('Letter delivered successfully!');
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.response?.data?.reason || 'Failed to deliver letter.';
      toast.error(errorMsg);
    } finally {
      setIsDelivering(false);
    }
  };

  const getDeliveryTriggerDescription = (letter) => {
    const type = letter.deliver_on;
    if (type === 'MILESTONE' && letter.milestone_name) {
      return { label: 'Milestone', icon: letter.milestone_icon || '🎯', description: letter.milestone_name };
    }
    if (type === 'EVENT' && letter.delivery_event) {
      return { label: 'Event', icon: '📅', description: letter.delivery_event };
    }
    if (type === 'DATE' && letter.fallback_date) {
      const date = new Date(letter.fallback_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
      return { label: 'Scheduled Date', icon: '📅', description: date };
    }
    if (type === 'INSTANT') {
      return { label: 'Instant Delivery', icon: '⚡', description: 'Deliver immediately' };
    }
    if (letter.fallback_date) {
      const date = new Date(letter.fallback_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
      return { label: 'Fallback Date', icon: '📅', description: date };
    }
    return { label: 'Manual Delivery', icon: '✉️', description: 'No trigger configured' };
  };

  const handleOpenForYouLetter = (letter) => {
    if (letter.viewStatus === 'locked') {
      toast.info(`This letter will unlock ${letter.unlockCondition || 'soon'}`);
    } else {
      setSelectedCapsule(letter);
    }
  };

  const handleMarkViewed = async (capsuleId) => {
    setForYouLetters(prev => prev.map(l =>
      l.id === capsuleId ? { ...l, viewStatus: 'read' } : l
    ));
    toast.success('Letter marked as read');
  };

  // Get next delivery info for activity bar
  const getNextDelivery = () => {
    const dateScheduled = myLetters
      .filter(l => l.status === 'SCHEDULED' && l.delivery_datetime)
      .sort((a, b) => new Date(a.delivery_datetime) - new Date(b.delivery_datetime));

    if (dateScheduled.length > 0) {
      const next = dateScheduled[0];
      const date = new Date(next.delivery_datetime);
      const recipientName = `${next.receiver_first_name || ''}'s`.trim();
      return `${recipientName} (${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})`;
    }

    const milestoneScheduled = myLetters
      .filter(l => l.status === 'SCHEDULED' && l.deliver_on === 'MILESTONE' && !l.delivery_datetime);

    if (milestoneScheduled.length > 0) {
      const next = milestoneScheduled[0];
      const recipientName = `${next.receiver_first_name || ''}'s`.trim();
      return `${recipientName} (${next.milestone_name || next.delivery_event || 'milestone'})`;
    }

    const eventScheduled = myLetters
      .filter(l => l.status === 'SCHEDULED' && l.deliver_on === 'EVENT' && !l.delivery_datetime);

    if (eventScheduled.length > 0) {
      const next = eventScheduled[0];
      const recipientName = `${next.receiver_first_name || ''}'s`.trim();
      return `${recipientName} (${next.delivery_event || 'event'})`;
    }

    return 'No scheduled letters';
  };

  // ─── Delivery Confirmation Dialog ───
  const renderDeliverConfirmDialog = () => {
    if (!deliverConfirm) return null;

    const trigger = getDeliveryTriggerDescription(deliverConfirm);
    const recipientName = `${deliverConfirm.receiver_first_name || ''} ${deliverConfirm.receiver_last_name || ''}`.trim() || 'the recipient';

    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[70] p-4">
        <div
          className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
          onClick={e => e.stopPropagation()}
        >
          <div className="px-6 pt-6 pb-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: colors.orangeTint }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={colors.orange} strokeWidth="2">
                  <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-bold" style={{ color: colors.text }}>Deliver This Letter?</h3>
                <p className="text-sm" style={{ color: colors.textMid }}>This action cannot be undone</p>
              </div>
            </div>

            <div className="rounded-xl p-4 mb-4" style={{ background: colors.creamWarm, border: '1px solid #E8DCC8' }}>
              <p className="font-semibold text-base mb-2" style={{ color: colors.text }}>
                {deliverConfirm.title || 'Untitled Letter'}
              </p>
              <p className="text-sm mb-3" style={{ color: colors.textMid }}>To: {recipientName}</p>
              <div className="rounded-lg p-3 flex items-start gap-3" style={{ background: '#FFFFFF', border: '1px solid #E8DCC8' }}>
                <span className="text-xl flex-shrink-0 mt-0.5">{trigger.icon}</span>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide mb-0.5" style={{ color: colors.textLight }}>{trigger.label}</p>
                  <p className="text-sm font-medium" style={{ color: colors.text }}>{trigger.description}</p>
                </div>
              </div>
            </div>

            <div className="rounded-xl p-4 mb-4" style={{ background: '#FFF8E1', border: '1.5px solid #FFE082' }}>
              <div className="flex items-start gap-3">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={colors.orange} strokeWidth="2" className="flex-shrink-0 mt-0.5">
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                <p className="text-sm leading-relaxed" style={{ color: '#5D4037' }}>
                  You are overriding the scheduled delivery trigger. The recipient will be able to read this letter <strong>immediately</strong> after delivery.
                </p>
              </div>
            </div>

            <label
              className="flex items-start gap-3 cursor-pointer select-none p-3 transition-colors"
              style={{
                background: deliverChecked ? colors.greenTint : 'transparent',
                border: deliverChecked ? '1.5px solid #A5D6A7' : '1.5px solid #E0E0E0',
                borderRadius: '12px',
              }}
            >
              <input
                type="checkbox"
                checked={deliverChecked}
                onChange={e => setDeliverChecked(e.target.checked)}
                className="mt-0.5 w-5 h-5 rounded flex-shrink-0"
                style={{ accentColor: colors.primary }}
              />
              <span className="text-sm leading-relaxed" style={{ color: colors.text }}>
                I confirm I want to deliver this letter now, bypassing the original schedule.
              </span>
            </label>
          </div>

          <div className="px-6 pb-6 flex gap-3">
            <button
              onClick={() => { setDeliverConfirm(null); setDeliverChecked(false); }}
              disabled={isDelivering}
              className="flex-1 px-4 py-2.5 text-sm font-semibold rounded-xl transition-colors"
              style={{ border: '1.5px solid #E0E0E0', color: colors.textMid, background: '#fff' }}
            >
              Cancel
            </button>
            <button
              onClick={handleDeliverConfirm}
              disabled={isDelivering || !deliverChecked}
              className="flex-1 px-4 py-2.5 text-sm font-semibold text-white rounded-xl transition-colors flex items-center justify-center gap-2"
              style={{
                background: !deliverChecked ? '#BDBDBD' : isDelivering ? '#BF4500' : colors.orange,
                cursor: !deliverChecked ? 'not-allowed' : 'pointer',
              }}
            >
              {isDelivering ? (
                <>
                  <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>
                  Delivering...
                </>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                  Confirm Delivery
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ─── Loading State ───
  const isLoading = myLettersLoading || forYouLoading;

  if (isLoading) {
    return (
      <HubLayout>
        <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: colors.cream }}>
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: colors.primary }} />
            <div className="text-lg font-medium" style={{ color: colors.text }}>Loading your letters...</div>
          </div>
        </div>
      </HubLayout>
    );
  }

  // ─── Filter pills for sending tab ───
  const filterPills = [
    { id: 'all', label: `All ${myLettersStats.all}` },
    { id: 'vault', label: `In Vault ${myLettersStats.vault || ''}`.trim() },
    { id: 'delivered', label: `Delivered ${myLettersStats.delivered || ''}`.trim() },
    { id: 'opened', label: `Opened ${myLettersStats.opened || ''}`.trim() },
    { id: 'draft', label: `Drafts ${myLettersStats.drafts || ''}`.trim() },
  ];

  // ─── Tab config ───
  const isSending = activeTab === 'sending';
  const isReceiving = activeTab === 'receiving';

  return (
    <HubLayout>
      <style>{animationCSS}</style>

      <div className="tc-page" style={{ maxWidth: '960px', margin: '0 auto', padding: '28px 24px 60px' }}>

        {/* ═══ Page Header ═══ */}
        <div className="tc-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{
              width: '44px', height: '44px', borderRadius: '12px', backgroundColor: colors.primary,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                <polyline points="22,6 12,13 2,6"/>
              </svg>
            </div>
            <h1 style={{ fontFamily: "'Lora', serif", fontSize: '26px', fontWeight: 600, color: colors.text }}>
              Time Capsules
            </h1>
          </div>
          <button
            onClick={() => navigate('/letter-creation')}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              fontFamily: "'Source Sans 3', sans-serif", fontSize: '15px', fontWeight: 600,
              padding: '12px 22px', borderRadius: '8px', border: 'none',
              backgroundColor: colors.primary, color: '#fff', cursor: 'pointer',
            }}
          >
            <Plus size={16} />
            Write New Letter
          </button>
        </div>
        <p className="tc-subtitle" style={{ color: colors.textMid, fontSize: '15px', marginBottom: '24px', paddingLeft: '58px' }}>
          Write letters for your family and open treasures they've left for you.
        </p>

        {/* ═══ Activity Bar ═══ */}
        <div className="tc-activity-bar" style={{
          display: 'flex', gap: 0, background: '#fff', border: `1px solid ${colors.borderLight}`,
          borderRadius: '12px', overflow: 'hidden', marginBottom: '28px',
        }}>
          {/* Next delivery */}
          <div className="tc-activity-item" style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '12px', padding: '16px 20px', borderRight: `1px solid ${colors.borderLight}` }}>
            <div style={{
              width: '36px', height: '36px', borderRadius: '10px', backgroundColor: colors.greenTint, color: colors.primary,
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
            </div>
            <div>
              <div style={{ fontSize: '12px', color: colors.textLight }}>Next delivery</div>
              <div style={{ fontWeight: 600, fontSize: '14px', color: colors.text }}>{getNextDelivery()}</div>
            </div>
          </div>

          {/* Ready to open */}
          <div className="tc-activity-item" style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '12px', padding: '16px 20px', borderRight: `1px solid ${colors.borderLight}` }}>
            <div style={{
              width: '36px', height: '36px', borderRadius: '10px', backgroundColor: colors.orangeTint, color: colors.orange,
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                <polyline points="22,6 12,13 2,6"/>
              </svg>
            </div>
            <div>
              <div style={{ fontSize: '12px', color: colors.textLight }}>Ready to open</div>
              <div style={{ fontWeight: 600, fontSize: '14px', color: forMeStats.ready > 0 ? colors.orange : colors.text }}>
                {forMeStats.ready > 0 ? `${forMeStats.ready} letter${forMeStats.ready > 1 ? 's' : ''} ready!` : 'None right now'}
              </div>
            </div>
          </div>

          {/* Family engagement */}
          <div className="tc-activity-item" style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '12px', padding: '16px 20px' }}>
            <div style={{
              width: '36px', height: '36px', borderRadius: '10px', backgroundColor: '#f3e5f5', color: '#7B1FA2',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/>
              </svg>
            </div>
            <div>
              <div style={{ fontSize: '12px', color: colors.textLight }}>Family engagement</div>
              <div style={{ fontWeight: 600, fontSize: '14px', color: colors.text }}>
                {myLettersStats.opened > 0 ? `${myLettersStats.opened} letter${myLettersStats.opened > 1 ? 's' : ''} opened` : 'No letters opened yet'}
              </div>
            </div>
          </div>
        </div>

        {/* ═══ Senior-Friendly Tabs ═══ */}
        <div className="tc-tabs" style={{
          display: 'flex', gap: 0, background: '#fff', borderRadius: '14px',
          border: `2px solid ${colors.border}`, overflow: 'hidden', marginBottom: '28px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        }}>
          {/* Sending Tab */}
          <button
            className="tc-tab-sending"
            onClick={() => setActiveTab('sending')}
            style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '14px',
              minHeight: '72px', padding: '14px 24px',
              fontFamily: "'Source Sans 3', sans-serif", fontSize: '18px', fontWeight: 600,
              border: 'none', borderRight: `2px solid ${colors.border}`,
              cursor: 'pointer', position: 'relative',
              background: isSending ? colors.greenTint : '#fff',
              color: isSending ? colors.primary : colors.textMid,
              transition: 'all 0.2s ease',
            }}
          >
            <div style={{
              width: '42px', height: '42px', borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              backgroundColor: isSending ? colors.primary : colors.panel,
              transition: 'all 0.2s',
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={isSending ? '#fff' : colors.textMid} strokeWidth="2">
                <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/>
              </svg>
            </div>
            <div style={{ textAlign: 'left' }}>
              <div style={{ fontSize: '18px', fontWeight: 700, lineHeight: 1.2, color: isSending ? colors.primary : colors.textMid, transition: 'color 0.2s' }}>
                Letters I'm Sending
              </div>
              <div className="tc-tab-sub" style={{ fontSize: '13px', fontWeight: 400, opacity: 0.7, lineHeight: 1.3 }}>
                Write to your children and grandchildren
              </div>
              {isSending && (
                <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px', marginTop: '2px', color: colors.primary }}>
                  ● You are here
                </div>
              )}
            </div>
            <div style={{
              minWidth: '28px', height: '28px', borderRadius: '14px', fontSize: '14px', fontWeight: 800,
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 8px',
              backgroundColor: isSending ? colors.primary : colors.panel,
              color: isSending ? '#fff' : colors.textLight,
              transition: 'all 0.2s',
            }}>
              {myLettersStats.all}
            </div>
            {/* Bottom indicator bar */}
            {isSending && (
              <div style={{
                position: 'absolute', bottom: 0, left: '16px', right: '16px', height: '4px',
                borderRadius: '4px 4px 0 0', backgroundColor: colors.primary,
              }} />
            )}
          </button>

          {/* Receiving Tab */}
          <button
            onClick={() => setActiveTab('receiving')}
            style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '14px',
              minHeight: '72px', padding: '14px 24px',
              fontFamily: "'Source Sans 3', sans-serif", fontSize: '18px', fontWeight: 600,
              border: 'none', cursor: 'pointer', position: 'relative',
              background: isReceiving ? colors.orangeTint : '#fff',
              color: isReceiving ? colors.orange : colors.textMid,
              transition: 'all 0.2s ease',
            }}
          >
            <div style={{
              width: '42px', height: '42px', borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              backgroundColor: isReceiving ? colors.orange : colors.panel,
              transition: 'all 0.2s',
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={isReceiving ? '#fff' : colors.textMid} strokeWidth="2">
                <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
              </svg>
            </div>
            <div style={{ textAlign: 'left' }}>
              <div style={{ fontSize: '18px', fontWeight: 700, lineHeight: 1.2, color: isReceiving ? colors.orange : colors.textMid, transition: 'color 0.2s' }}>
                Letters for Me
              </div>
              <div className="tc-tab-sub" style={{ fontSize: '13px', fontWeight: 400, opacity: 0.7, lineHeight: 1.3 }}>
                From people who love you
              </div>
              {isReceiving && (
                <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px', marginTop: '2px', color: colors.orange }}>
                  ● You are here
                </div>
              )}
            </div>
            <div style={{
              minWidth: '28px', height: '28px', borderRadius: '14px', fontSize: '14px', fontWeight: 800,
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 8px',
              backgroundColor: isReceiving ? colors.orange : (forMeStats.ready + forMeStats.pending > 0 ? colors.orange : colors.panel),
              color: isReceiving ? '#fff' : (forMeStats.ready + forMeStats.pending > 0 ? '#fff' : colors.textLight),
              transition: 'all 0.2s',
              animation: forMeStats.ready > 0 && !isReceiving ? 'pulse-dot 2s infinite' : 'none',
            }}>
              {forMeStats.ready + forMeStats.pending}
            </div>
            {isReceiving && (
              <div style={{
                position: 'absolute', bottom: 0, left: '16px', right: '16px', height: '4px',
                borderRadius: '4px 4px 0 0', backgroundColor: colors.orange,
              }} />
            )}
          </button>
        </div>

        {/* ═══════════════════════════════════════
             TAB CONTENT: SENDING
           ═══════════════════════════════════════ */}
        {isSending && (
          <div>
            {/* Filter Pills */}
            <div className="tc-filter-pills" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '18px' }}>
              {filterPills.map(pill => (
                <button
                  key={pill.id}
                  onClick={() => setMyLettersFilter(pill.id)}
                  style={{
                    fontFamily: "'Source Sans 3', sans-serif", fontSize: '14px', fontWeight: 600,
                    padding: '8px 16px', borderRadius: '20px', minHeight: '40px',
                    border: `1.5px solid ${myLettersFilter === pill.id ? colors.primary : colors.border}`,
                    backgroundColor: myLettersFilter === pill.id ? colors.primary : '#fff',
                    color: myLettersFilter === pill.id ? '#fff' : colors.textMid,
                    cursor: 'pointer', transition: 'all 0.15s',
                  }}
                >
                  {pill.label}
                </button>
              ))}
            </div>

            {/* Letter Cards */}
            {filteredMyLetters.length > 0 ? (
              <div className="tc-card-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                {filteredMyLetters.map(letter => (
                  <LetterCard
                    key={letter.id}
                    letter={letter}
                    onClick={handleViewMyLetter}
                    onEdit={handleEdit}
                    onPreview={handlePreview}
                    onShare={() => {}}
                    onDeliver={handleDeliver}
                    videoThumbnails={videoThumbnails}
                  />
                ))}
              </div>
            ) : (
              <div style={{
                textAlign: 'center', padding: '60px 20px', background: '#fff',
                borderRadius: '12px', border: `1px solid ${colors.borderLight}`,
              }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>📝</div>
                <h3 style={{ fontFamily: "'Lora', serif", fontSize: '18px', fontWeight: 600, marginBottom: '8px', color: colors.text }}>
                  No letters found
                </h3>
                <p style={{ fontSize: '14px', color: colors.textMid, marginBottom: '24px' }}>
                  Create your first time capsule letter!
                </p>
                <button
                  onClick={() => navigate('/letter-creation')}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                    fontFamily: "'Source Sans 3', sans-serif", fontSize: '15px', fontWeight: 600,
                    padding: '12px 24px', borderRadius: '8px', border: 'none',
                    backgroundColor: colors.primary, color: '#fff', cursor: 'pointer',
                  }}
                >
                  <Plus size={16} /> Create Letter
                </button>
              </div>
            )}
          </div>
        )}

        {/* ═══════════════════════════════════════
             TAB CONTENT: RECEIVING
           ═══════════════════════════════════════ */}
        {isReceiving && (
          <div>
            {/* Search & Filter Bar */}
            {(readyLetters.length + openedLetters.length) > 0 && (
              <div style={{ marginBottom: '20px' }}>
                {/* Search input */}
                <div style={{ position: 'relative', marginBottom: '10px' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={colors.textMid} strokeWidth="2"
                    style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)' }}>
                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                  <input
                    type="text"
                    placeholder="Search letters by title, sender, or keyword..."
                    value={forMeSearch}
                    onChange={(e) => setForMeSearch(e.target.value)}
                    style={{
                      width: '100%', padding: '10px 14px 10px 40px', fontSize: '14px',
                      border: `1.5px solid ${colors.borderLight}`, borderRadius: '10px',
                      outline: 'none', fontFamily: "'Source Sans 3', sans-serif",
                      backgroundColor: '#fff', boxSizing: 'border-box',
                      transition: 'border-color 0.15s',
                    }}
                    onFocus={(e) => { e.target.style.borderColor = colors.primary; }}
                    onBlur={(e) => { e.target.style.borderColor = colors.borderLight; }}
                  />
                </div>
                {/* Person filter pills */}
                {forMePeople.length > 1 && (
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => setForMePersonFilter('all')}
                      style={{
                        padding: '5px 14px', borderRadius: '20px', fontSize: '13px', fontWeight: 600,
                        border: `1.5px solid ${forMePersonFilter === 'all' ? colors.primary : colors.borderLight}`,
                        backgroundColor: forMePersonFilter === 'all' ? colors.primary : '#fff',
                        color: forMePersonFilter === 'all' ? '#fff' : colors.textMid,
                        cursor: 'pointer', fontFamily: "'Source Sans 3', sans-serif", transition: 'all 0.15s',
                      }}
                    >
                      All
                    </button>
                    {forMePeople.map(p => {
                      const active = forMePersonFilter === p.key;
                      const hue = (p.label.charCodeAt(0) * 47) % 360;
                      return (
                        <button
                          key={p.key}
                          onClick={() => setForMePersonFilter(active ? 'all' : p.key)}
                          style={{
                            padding: '5px 14px', borderRadius: '20px', fontSize: '13px', fontWeight: 600,
                            border: `1.5px solid ${active ? `hsl(${hue}, 55%, 45%)` : colors.borderLight}`,
                            backgroundColor: active ? `hsl(${hue}, 55%, 93%)` : '#fff',
                            color: active ? `hsl(${hue}, 55%, 30%)` : colors.textMid,
                            cursor: 'pointer', fontFamily: "'Source Sans 3', sans-serif", transition: 'all 0.15s',
                          }}
                        >
                          {p.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Ready to Open Section */}
            {filteredReadyLetters.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                  <div style={{
                    width: '10px', height: '10px', borderRadius: '50%', flexShrink: 0,
                    backgroundColor: colors.orange, animation: 'pulse-dot 2s infinite',
                  }} />
                  <h2 style={{ fontFamily: "'Lora', serif", fontSize: '18px', fontWeight: 600, color: colors.orange }}>
                    Ready to Open
                  </h2>
                </div>

                {filteredReadyLetters.map(letter => (
                  <div
                    className="tc-ready-card"
                    key={letter.id}
                    onClick={() => handleOpenForYouLetter(letter)}
                    style={{
                      background: '#fff', border: `2px solid ${colors.orange}`, borderRadius: '14px',
                      padding: '18px 20px', display: 'flex', alignItems: 'center', gap: '16px',
                      position: 'relative', overflow: 'hidden', marginBottom: '12px',
                      cursor: 'pointer', transition: 'box-shadow 0.15s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(230,81,0,0.12)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
                  >
                    {/* Top orange bar */}
                    <div style={{
                      position: 'absolute', top: 0, left: 0, right: 0, height: '3px',
                      background: `linear-gradient(90deg, ${colors.orange}, ${colors.orangeLight})`,
                    }} />

                    {/* Icon */}
                    <div style={{
                      width: '48px', height: '48px', borderRadius: '50%', flexShrink: 0,
                      background: 'linear-gradient(135deg, #FFF3E0, #FFE0B2)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={colors.orange} strokeWidth="2">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                        <polyline points="22,6 12,13 2,6"/>
                      </svg>
                    </div>

                    {/* Info */}
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px', flexWrap: 'wrap' }}>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: '4px',
                          fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px',
                          padding: '3px 10px', borderRadius: '10px',
                          backgroundColor: colors.orangeTint, color: colors.orange,
                        }}>
                          ✨ Unlocked
                        </span>
                        {(() => {
                          const receiverName = `${letter.receiver_first_name || ''} ${letter.receiver_last_name || ''}`.trim();
                          const userData = getUser() || {};
                          const isForSelf = letter.receiver === userData.person_id;
                          if (receiverName && !isForSelf) {
                            // Color based on first char of name for consistency
                            const hue = (receiverName.charCodeAt(0) * 47) % 360;
                            return (
                              <span style={{
                                display: 'inline-flex', alignItems: 'center', gap: '3px',
                                fontSize: '11px', fontWeight: 700, padding: '3px 10px', borderRadius: '10px',
                                backgroundColor: `hsl(${hue}, 55%, 93%)`, color: `hsl(${hue}, 55%, 35%)`,
                              }}>
                                For {letter.receiver_first_name}
                              </span>
                            );
                          }
                          return null;
                        })()}
                      </div>
                      <div style={{ fontFamily: "'Lora', serif", fontSize: '16px', fontWeight: 600, color: colors.text }}>
                        {letter.title || 'A Special Letter'}
                      </div>
                      <div style={{ fontSize: '13px', color: colors.textMid, marginTop: '2px' }}>
                        From: {letter.senderName} · Letter
                      </div>
                    </div>

                    {/* Open Button */}
                    <button
                      className="tc-ready-btn"
                      onClick={(e) => { e.stopPropagation(); handleOpenForYouLetter(letter); }}
                      style={{
                        fontFamily: "'Source Sans 3', sans-serif", fontSize: '15px', fontWeight: 700,
                        padding: '10px 22px', borderRadius: '10px', border: 'none',
                        backgroundColor: colors.orange, color: '#fff', cursor: 'pointer', flexShrink: 0,
                        boxShadow: '0 2px 8px rgba(230,81,0,0.2)', transition: 'all 0.15s',
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      }}
                    >
                      Open ✉
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Mystery Vault */}
            {pendingCount > 0 && (
              <div style={{ margin: '24px 0' }}>
                <MysteryVault pendingCount={pendingCount} />
              </div>
            )}

            {/* Family Time Capsule Activity */}
            {familyActivity.length > 0 && (
              <div style={{
                background: '#fff', border: `1px solid ${colors.borderLight}`, borderRadius: '12px',
                padding: '20px', marginBottom: '24px',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                  <div style={{
                    width: '32px', height: '32px', borderRadius: '8px', backgroundColor: colors.greenTint,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={colors.primary} strokeWidth="2">
                      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
                      <circle cx="9" cy="7" r="4"/>
                      <path d="M23 21v-2a4 4 0 00-3-3.87"/>
                      <path d="M16 3.13a4 4 0 010 7.75"/>
                    </svg>
                  </div>
                  <div>
                    <div style={{ fontFamily: "'Lora', serif", fontSize: '16px', fontWeight: 600, color: colors.text }}>
                      Family Time Capsule Activity
                    </div>
                    <div style={{ fontSize: '13px', color: colors.textMid }}>
                      Letters waiting in the vault for your family
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {familyActivity.map(member => (
                    <div
                      key={member.person_id}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '12px',
                        padding: '12px 14px', borderRadius: '10px',
                        backgroundColor: colors.creamWarm,
                        border: `1px solid ${colors.borderLight}`,
                      }}
                    >
                      {/* Avatar */}
                      <div style={{
                        width: '36px', height: '36px', borderRadius: '50%',
                        backgroundColor: colors.primary, display: 'flex',
                        alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        color: '#fff', fontWeight: 700, fontSize: '13px',
                      }}>
                        {member.is_minor
                          ? '\uD83D\uDC76'
                          : getInitials(member.first_name, member.last_name)
                        }
                      </div>

                      {/* Name + role */}
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '15px', fontWeight: 600, color: colors.text }}>
                          {`${member.first_name} ${member.last_name}`.trim()}
                        </div>
                        {member.role && (
                          <div style={{ fontSize: '12px', color: colors.textLight, textTransform: 'capitalize' }}>
                            {member.role}
                          </div>
                        )}
                      </div>

                      {/* Waiting count badge */}
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: '4px',
                        fontSize: '12px', fontWeight: 700,
                        padding: '4px 10px', borderRadius: '12px',
                        backgroundColor: '#FFF8E1', color: '#F57F17',
                        border: '1px solid #FFE082',
                      }}>
                        {member.waiting_count} letter{member.waiting_count !== 1 ? 's' : ''} waiting
                      </span>

                      {/* In vault label */}
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: '4px',
                        fontSize: '12px', fontWeight: 600, color: colors.primary,
                      }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
                        </svg>
                        In vault
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* How it works */}
            <div style={{
              background: '#fff', border: `1px solid ${colors.borderLight}`, borderRadius: '12px',
              padding: '18px 20px', marginBottom: '24px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={colors.textLight} strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                </svg>
                <span style={{ fontSize: '14px', fontWeight: 600, color: colors.text }}>How it works</span>
              </div>
              <p style={{ fontSize: '14px', color: colors.textMid, lineHeight: 1.6 }}>
                When your family adds a milestone to your life story — like a birthday, graduation, or new job — we'll automatically check if any treasures are waiting to be released for that moment.
              </p>
            </div>

            {/* Previously Opened */}
            {filteredOpenedLetters.length > 0 && (
              <div style={{ marginTop: '28px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                  <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: colors.primary, flexShrink: 0 }} />
                  <h2 style={{ fontFamily: "'Lora', serif", fontSize: '18px', fontWeight: 600, color: colors.primary }}>
                    Previously Opened
                  </h2>
                </div>

                {filteredOpenedLetters.map(letter => (
                  <div
                    key={letter.id}
                    onClick={() => handleOpenForYouLetter(letter)}
                    style={{
                      backgroundColor: colors.panel, border: `1px solid ${colors.borderLight}`, borderRadius: '12px',
                      padding: '16px 18px', display: 'flex', alignItems: 'center', gap: '14px',
                      marginBottom: '10px', cursor: 'pointer', transition: 'box-shadow 0.15s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.06)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
                  >
                    {/* Icon */}
                    <div style={{
                      width: '40px', height: '40px', borderRadius: '50%', backgroundColor: colors.greenTint,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={colors.primary} strokeWidth="2">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                        <polyline points="22,6 12,13 2,6"/>
                      </svg>
                    </div>

                    {/* Info */}
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px', flexWrap: 'wrap' }}>
                        <div style={{ fontFamily: "'Lora', serif", fontSize: '15px', fontWeight: 600, color: colors.text }}>
                          {letter.title || 'Letter'}
                        </div>
                        {(() => {
                          const rName = `${letter.receiver_first_name || ''} ${letter.receiver_last_name || ''}`.trim();
                          const ud = getUser() || {};
                          if (rName && letter.receiver !== ud.person_id) {
                            const h = (rName.charCodeAt(0) * 47) % 360;
                            return (
                              <span style={{
                                fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '8px',
                                backgroundColor: `hsl(${h}, 55%, 93%)`, color: `hsl(${h}, 55%, 35%)`,
                              }}>
                                For {letter.receiver_first_name}
                              </span>
                            );
                          }
                          return null;
                        })()}
                      </div>
                      <div style={{ fontSize: '13px', color: colors.textMid }}>
                        {letter.milestone_name ? `For ${letter.milestone_name} · ` : ''}From {letter.senderName}
                      </div>
                      {letter.viewed_at && (
                        <div style={{ fontSize: '13px', color: colors.textMid, marginTop: '2px' }}>
                          Opened {new Date(letter.viewed_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                        </div>
                      )}
                    </div>

                    {/* Read Again button */}
                    <button
                      onClick={(e) => { e.stopPropagation(); handleOpenForYouLetter(letter); }}
                      style={{
                        fontFamily: "'Source Sans 3', sans-serif", fontSize: '13px', fontWeight: 600,
                        padding: '8px 14px', borderRadius: '8px',
                        border: `1.5px solid ${colors.border}`, backgroundColor: '#fff',
                        color: colors.primary, cursor: 'pointer', flexShrink: 0,
                        transition: 'all 0.15s',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = colors.primary; e.currentTarget.style.backgroundColor = colors.greenTint; }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = colors.border; e.currentTarget.style.backgroundColor = '#fff'; }}
                    >
                      Read Again
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Empty state for receiving tab */}
            {filteredReadyLetters.length === 0 && filteredOpenedLetters.length === 0 && pendingCount === 0 && !forMeSearch && forMePersonFilter === 'all' && (
              <div style={{
                textAlign: 'center', padding: '60px 20px', background: '#fff',
                borderRadius: '12px', border: `1px solid ${colors.borderLight}`,
              }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>💌</div>
                <h3 style={{ fontFamily: "'Lora', serif", fontSize: '18px', fontWeight: 600, marginBottom: '8px', color: colors.text }}>
                  No Letters Yet
                </h3>
                <p style={{ fontSize: '14px', color: colors.textMid }}>
                  When family members send you letters, they'll appear here.
                </p>
                <p style={{ fontSize: '14px', color: colors.textLight, marginTop: '4px' }}>
                  Letters unlock at special moments — birthdays, graduations, and more!
                </p>
              </div>
            )}
          </div>
        )}

        {/* ═══ Tip Banner ═══ */}
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '14px 18px',
          backgroundColor: colors.greenTint, borderRadius: '12px', fontSize: '14px', color: colors.primary,
          marginTop: '28px',
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0, marginTop: '2px' }}>
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
          </svg>
          <span>
            <strong>Tip:</strong>&nbsp;"Letters I'm Sending" is where you write letters for your family. "Letters for Me" is where you open letters your family wrote for you.
          </span>
        </div>
      </div>

      {/* ═══ Capsule Viewer Modal ═══ */}
      {selectedCapsule && (
        <CapsuleViewer
          capsule={selectedCapsule}
          onClose={() => setSelectedCapsule(null)}
          onMarkViewed={handleMarkViewed}
          onDeliver={handleDeliver}
          videoThumbnails={videoThumbnails}
        />
      )}

      {/* ═══ Deliver Confirmation Dialog ═══ */}
      {renderDeliverConfirmDialog()}
    </HubLayout>
  );
}
